import { create } from 'zustand';
import axiosClient from '../../src/services/api';

const useCourseStore = create((set, get) => ({
  courses: [],
  enrolledCourses: [],
  course: null,
  wishlist: [],
  recentlyViewed: [],
  searchResults: [],
  categories: [],
  isLoading: false,
  error: null,

  fetchCategories: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/courses/categories');
      set({ categories: response.data.data, isLoading: false });
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to fetch users',
        isLoading: false,
      });
    }
  },

  searchCourses: async (query) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/courses', {
        params: {
          search: query,
          page: 1,
          limit: 10
        }
      });
      set({ searchResults: response.data.data, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to search courses', isLoading: false });
    }
  },

  fetchCourses: async (filters = {}) => {
    set({ isLoading: true, error: null });
    try {
      const params = new URLSearchParams();

      Object.keys(filters).forEach(key => {
        if (filters[key] !== '' && filters[key] !== null && filters[key] !== undefined) {
          params.append(key, filters[key]);
        }
      });

      const response = await axiosClient.get(`/courses?${params.toString()}`);
      set({
        courses: response.data.data,
        isLoading: false
      });
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to fetch courses',
        isLoading: false
      });
      return null;
    }
  },

  fetchCourse: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get(`/courses/${id}`);

      const course = response.data.data;

      if (course.modules) {
        course.modules.sort((a, b) => (a.order || 0) - (b.order || 0));
        course.modules.forEach(module => {
          if (module.lessons) {
            module.lessons.sort((a, b) => (a.order || 0) - (b.order || 0));
          }
        });
      }

      set({
        course: course,
        isLoading: false
      });
      return course;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to fetch course',
        isLoading: false
      });
      return null;
    }
  },

   fetchISACourse: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get(`/courses/isa/${id}`);

      const course = response.data.data;

      if (course.modules) {
        course.modules.sort((a, b) => (a.order || 0) - (b.order || 0));
        course.modules.forEach(module => {
          if (module.lessons) {
            module.lessons.sort((a, b) => (a.order || 0) - (b.order || 0));
          }
        });
      }

      set({
        course: course,
        isLoading: false
      });
      return course;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to fetch course',
        isLoading: false
      });
      return null;
    }
  },

  fetchMyCourses: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/courses/my-courses');
      let courses = response.data.data;

      if (response.data.data && response.data.data.length > 0 && response.data.data[0].modules) {
        courses = courses.map(course => {
          if (course.modules) {
            course.modules.sort((a, b) => (a.order || 0) - (b.order || 0));
            course.modules.forEach(module => {
              if (module.lessons) {
                module.lessons.sort((a, b) => (a.order || 0) - (b.order || 0));
              }
            });
          }
          return course;
        });
      }

      set({
        enrolledCourses: courses,
        isLoading: false
      });
      return courses;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to fetch my courses',
        isLoading: false
      });
      return null;
    }
  },

  getWishlist: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/wishlist/wishlist');
      set({ wishlist: response.data.data, isLoading: false });
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to fetch wishlist',
        isLoading: false
      });
    }
  },

  addToWishlist: async (courseId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.post(`/wishlist/wishlist/${courseId}`);
      set({ wishlist: response.data.data, isLoading: false });
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to add to wishlist',
        isLoading: false
      });
      throw error;
    }
  },

  removeFromWishlist: async (courseId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.delete(`/wishlist/wishlist/${courseId}`);
      set({ wishlist: response.data.data, isLoading: false });
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to remove from wishlist',
        isLoading: false
      });
      throw error;
    }
  },

  generateDescription: async (context, type = 'course') => {
    try {
      const response = await axiosClient.post('/ai/generate-description', {
        context,
        type
      });
      return response.data.data.description;
    } catch (error) {
      console.error('Failed to generate description:', error);
      return null;
    }
  },

  markLessonComplete: async (lessonId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.post(`/learning/lessons/${lessonId}/complete`);

      set({ isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to mark lesson as complete', isLoading: false });
      return null;
    }
  },

  enrollInCourse: async (courseId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.post(`/courses/${courseId}/enroll`);
      set({ isLoading: false });
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to enroll in course',
        isLoading: false
      });
      return null;
    }
  },

  verifyPayment: async (reference) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.post('/payments/verify', { reference });
      set({ isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to verify payment', isLoading: false });
      return null;
    }
  },

  createCourse: async (courseData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.post('/courses', courseData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      set({ isLoading: false });
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to create course',
        isLoading: false
      });
      return null;
    }
  },

  updateCourse: async (id, courseData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.put(`/courses/${id}`, courseData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      set({ isLoading: false });
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to update course',
        isLoading: false
      });
      return null;
    }
  },

  getRecentlyViewed: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/recentlyviewed/recently-viewed');
      set({ recentlyViewed: response.data.courses, isLoading: false });
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to fetch recently viewed',
        isLoading: false
      });
    }
  },

  addToRecentlyViewed: async (courseId) => {
    try {
      const response = await axiosClient.post(`/recentlyviewed/recently-viewed/${courseId}`);
      set({ recentlyViewed: response.data.courses, isLoading: false });
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to add recently viewed',
        isLoading: false
      });
    }
  },

  clearError: () => set({ error: null }),
}));

export default useCourseStore;